import { computed, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { filter, Subject, switchMap } from 'rxjs';

import { RoutesDictionaryByCode } from '@legacy/constants';
import { RouteCodeEnum } from '@models/menu';
import { DataListType, MimeTypesEnum } from '@models/module/fields/main';
import { GridSearchEntityInstance } from '@models/module/grid';
import { GridFieldsModel, Role } from '@models/module/main';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { ProgressPaymentRow } from '@module-instance/progress-payment/common';
import { NgToJsService } from '@ng2/services';
import { ActionSubItem, EntityTableRow, TableDataService } from '@shared/components/data-list/common';
import { DATE_TIME_FORMATS } from '@shared/consts/global.consts';
import { ActionDialogData, ActionsDialogComponent, InfoDialog, UndoTransitionDialog } from '@shared/dialogs';
import { ConfirmationDialog, ConfirmationDialogData } from '@shared/dialogs/confirmation-dialog.component';
import { FileService, NavigationService, NotificationService, UserService } from '@shared/services';
import { PrintService } from '@shared/services/print.service';
import { SignalRService } from '@shared/services/signalR.service';
import { HubHandlerSourceAngular } from '@models/system/signalR';
import { PdfRegenerationProgress, PdfRegenerationResult } from '@module-instance/services/module-instance.service';
import { DictionaryStore, UserStore } from '@signal-store/index';
import {
  DmFiltersToApply,
  DmService,
  DmTableOptions,
  DmUrlActionEnum,
  EntityInstanceTransition,
  TransitionService,
} from './common';
import { DocumentInfoModel } from './document-details/common';
import { ModuleInstanceService } from '@module-instance/services/module-instance.service';

@Injectable({ providedIn: 'root' })
export class DmActionsService {
  private router = inject(Router);
  private routerService = inject(NavigationService);
  private ng2Js = inject(NgToJsService);
  private dialog = inject(MatDialog);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private dmService = inject(DmService);
  private fileService = inject(FileService);
  private printService = inject(PrintService);
  private notificationService = inject(NotificationService);
  private moduleInstanceService = inject(ModuleInstanceService);
  private signalRService = inject(SignalRService);
  private transitionService = inject(TransitionService);
  private tableDataService = inject(TableDataService);
  private userStore = inject(UserStore);
  private dictionaryStore = inject(DictionaryStore);

  currentModule = computed(() => this.userStore.menu.activeMenuItem());
  contractId = computed(() => this.userStore.contract()?.id);
  readonly pdfRegenerationResult$ = new Subject<PdfRegenerationResult>();
  readonly pdfRegenerationProgress$ = new Subject<PdfRegenerationProgress>();

  constructor() {
    this.signalRService.registerHandler(
      HubHandlerSourceAngular,
      'PdfRegenerationCompleted',
      (result: PdfRegenerationResult) => this.onPdfRegenerationCompleted(result),
    );
    this.signalRService.registerHandler(
      HubHandlerSourceAngular,
      'PdfRegenerationInProgress',
      (progress: PdfRegenerationProgress) => this.pdfRegenerationProgress$.next(progress),
    );
  }

  regeneratePdf(entityInstanceId: number) {
    this.moduleInstanceService.regeneratePdf(entityInstanceId).subscribe({
      next: () => this.notificationService.success('messages.pdfRegenerationStarted'),
    });
  }

  private onPdfRegenerationCompleted(result: PdfRegenerationResult) {
    this.pdfRegenerationResult$.next(result);
    if (result.failed === 0) {
      this.notificationService.success('messages.pdfRegenerated');
    } else if (result.succeeded === 0) {
      this.notificationService.error('messages.pdfRegenerationFailed');
    } else {
      this.notificationService.warning('messages.pdfRegenerationPartial');
    }
  }

  openInfoDialog(information: string) {
    this.dialog.open(InfoDialog, {
      panelClass: ['cipo-dialog', 'classic'],
      ...this.userService.getResponsiveDialogSize().xs,
      data: {
        title: this.translate.instant('dm.infoTitle', { module: this.userStore.menu.activeMenuItem().name }),
        description: information,
        isFroala: true,
      },
    });
  }

  importDocuments() {
    this.ng2Js.importDocuments(this.currentModule().moduleId, this.currentModule().code);
  }

  downloadLogs(tableOptions: DmTableOptions, filtersToApply: DmFiltersToApply, dataListType?: DataListType) {
    const searchParams = this.dmService.getSearchParams(tableOptions, filtersToApply);
    delete searchParams.pagesize;
    this.dmService
      .getAsExcel(this.contractId(), this.currentModule().moduleId, searchParams, dataListType)
      .subscribe(response => {
        const module = this.currentModule();
        const contractPart = module.perContract ? `-${this.userStore.contract()?.name}` : '';
        const fileName = `${module.name}${contractPart}-${moment().format(DATE_TIME_FORMATS.date)}.xlsx`;
        const mimeType = MimeTypesEnum.XLSX;
        this.fileService.downloadBase64File(response, fileName, mimeType);
      });
  }

  printLogs(
    tableOptions: DmTableOptions,
    filtersToApply: DmFiltersToApply,
    gridFields: GridFieldsModel,
    dataListType?: DataListType,
  ) {
    const params = this.dmService.getSearchParams(tableOptions, filtersToApply);
    delete params.pagesize;
    const columns = this.tableDataService.getTableColumns((gridFields?.fields ?? []).filter(f => f.printable));
    this.dmService
      .getTableData({
        contractId: this.contractId(),
        moduleId: this.currentModule().moduleId,
        search: params,
        dataListType,
      })
      .subscribe(({ data }) => {
        const rows = this.tableDataService.mapTableRowsState(data);
        this.printService.printTable(columns, rows);
      });
  }

  openReports() {
    this.ng2Js.openReportsDialog(this.currentModule().code);
  }

  downloadDocuments(
    selectedRows: (GridSearchEntityInstance | EntityTableRow | DocumentInfoModel)[],
    row?: GridSearchEntityInstance | EntityTableRow | DocumentInfoModel,
  ) {
    const rows = row ? [row] : selectedRows;
    this.openActionDialog(rows, 'download');
  }

  emailDocuments(
    selectedRows: (GridSearchEntityInstance | EntityTableRow | DocumentInfoModel)[],
    row?: GridSearchEntityInstance | EntityTableRow | DocumentInfoModel,
  ) {
    const rows = row ? [row] : selectedRows;
    this.openActionDialog(rows, 'email');
  }

  printDocuments(
    selectedRows: (GridSearchEntityInstance | EntityTableRow | DocumentInfoModel)[],
    row?: GridSearchEntityInstance | EntityTableRow | DocumentInfoModel,
  ) {
    const rows = row ? [row] : selectedRows;
    this.openActionDialog(rows, 'print');
  }

  private openActionDialog(
    rows: (GridSearchEntityInstance | EntityTableRow | DocumentInfoModel)[],
    actionType: 'download' | 'print' | 'email',
  ) {
    const contract = this.userStore.contract();

    if (!contract || !rows.length) return;

    const rg0 = rows[0] as GridSearchEntityInstance;
    const rd0 = rows[0] as DocumentInfoModel;
    const moduleId = rg0.module_id ?? rd0.moduleId;
    const module = this.userStore.menuItemsList().find(m => m.moduleId === moduleId);

    const entityInstances = rows.map(r => {
      const rg = r as GridSearchEntityInstance;
      const rd = r as DocumentInfoModel;
      return {
        entityInstanceId: rg.entity_instance_id ?? rd.entityInstanceId,
        documentNumber: rg.full_doc_num ?? rg.document_number ?? rd.documentNumber,
        fileName: rg.file_name as string | undefined,
        isClosedState:
          (rd.isClosedState ?? false) || (rg.is_closed_state ?? false) || (rg.closed_on ? rg.closed_on !== '-' : false),
        screenId: rg.screen_id ?? rd.screenId ?? 0,
      };
    });

    const contractId =
      rd0.contractId ||
      rg0.contract_id ||
      contract?.id ||
      (rg0.entity_instance_id && module.code === RouteCodeEnum.Contacts ? rg0.entity_instance_id : undefined);
    const contractNo = contract?.no || rg0.contract_no || rg0.full_doc_num;

    const isContractClosedOrArchived = !!(contract?.isClosed || contract?.isArchived);

    this.dialog.open<ActionsDialogComponent, ActionDialogData>(ActionsDialogComponent, {
      panelClass: ['cipo-dialog', 'classic'],
      ...this.userService.getResponsiveDialogSize().sm,
      data: {
        contractId,
        contractNo,
        moduleId,
        moduleName: module.name,
        moduleCode: module.code,
        entityInstances,
        actionType,
        isContractClosedOrArchived,
      },
    });
  }

  buildTransitionSubItems(
    row: EntityTableRow,
    transitions: Record<string, EntityInstanceTransition[]>,
    onReloaded: (entityInstanceId: number) => void,
    isContractClosedOrArchived = false,
  ): ActionSubItem<EntityTableRow>[] {
    const rowTransitions = transitions[String(row?.current_state_id)] ?? [];

    return (
      rowTransitions
        .filter(
          () =>
            !!row?.entity_instance_id &&
            !row?.needs_approval &&
            !row?.has_revisions &&
            !isContractClosedOrArchived &&
            (row?.isCurrent ?? row?.is_current ?? true),
        )
        /* Filter the transition roles for open state documents */
        .map(transition => {
          if (!row.is_draft && transition.roles?.length > 1) {
            return {
              ...transition,
              roles: transition.roles.slice(0, 1),
            };
          }
          return transition;
        })
        .map(transition => {
          const hasMultipleRoles = (transition.roles?.length ?? 0) > 1;

          if (hasMultipleRoles) {
            return {
              displayName: transition.name,
              subItems: transition.roles.map(role => ({
                displayName: role.value,
                onSelect: (r: EntityTableRow) =>
                  this.executeTransition([r], transition, role).subscribe(() => onReloaded(r.entity_instance_id)),
              })),
            };
          }

          return {
            displayName: transition.name,
            onSelect: (r: EntityTableRow) =>
              this.executeTransition([r], transition).subscribe(() => onReloaded(r.entity_instance_id)),
          };
        })
    );
  }
  executeTransition(rows: EntityTableRow[], transition: EntityInstanceTransition, role?: Role) {
    return this.transitionService.executeTransition({
      contractId: this.contractId(),
      rows,
      transition,
      role,
      workflowId: this.currentModule().workflowId,
    });
  }

  undoLastAction(
    selectedRows: (GridSearchEntityInstance | EntityTableRow)[],
    row?: GridSearchEntityInstance | EntityTableRow,
  ) {
    const rows = row ? [row] : selectedRows;
    const contractId = this.contractId();
    const moduleId = this.currentModule().moduleId;

    // Fetch module roles for notification options
    return this.dialog
      .open(UndoTransitionDialog, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().xs,
        data: {
          title: 'dm.undoLastAction.title',
          message: 'dm.undoLastAction.description',
          moduleId: moduleId,
          entityInstanceId: rows[0].entity_instance_id,
          contractId: contractId,
        },
      })
      .afterClosed()
      .pipe(
        filter(result => !!result?.confirmed),
        switchMap(result =>
          this.dmService.undoLastAction(
            rows as EntityTableRow[],
            {
              contractId,
              moduleId,
            },
            result.notifyUsers,
          ),
        ),
      );
  }

  openDocument(
    row: GridSearchEntityInstance | EntityTableRow | ProgressPaymentRow,
    clone: boolean,
    activatedRoute: ActivatedRoute,
  ) {
    const { module_id, entity_instance_id, screen_id, entity_template_id, payment_period_id } = row;
    if (module_id === SystemModulesEnum.Contracts) {
      this.ng2Js.editContract({
        itemId: entity_instance_id,
        entityInstanceId: entity_instance_id,
        screenId: screen_id,
        closed_on: (row as GridSearchEntityInstance).closed_on,
      });
    } else if (module_id === SystemModulesEnum.SOV) {
      this.routerService.tenantNavigate([
        RoutesDictionaryByCode[RouteCodeEnum.SOV],
        this.contractId(),
        entity_instance_id,
      ]);
    } else if (module_id === SystemModulesEnum.ProgressPayment) {
      this.routerService.tenantNavigate([
        RoutesDictionaryByCode[RouteCodeEnum.ProgressPayment],
        this.contractId(),
        payment_period_id,
        entity_instance_id,
      ]);
    } else {
      const urlActionId = clone
        ? DmUrlActionEnum.Clone
        : entity_template_id
          ? DmUrlActionEnum.Preset
          : DmUrlActionEnum.Create;

      /* If preset - id holds the entity template id. If clone or create - id holds the entity_instance_id of either current document or cloned one. */
      const id = entity_template_id ?? entity_instance_id;
      this.router.navigate(['details', urlActionId, id, screen_id], {
        relativeTo: activatedRoute,
      });
    }
  }

  deleteDocuments(
    selectedRows: (GridSearchEntityInstance | EntityTableRow)[],
    row?: GridSearchEntityInstance | EntityTableRow,
  ) {
    const rows = row ? [row] : selectedRows;
    return this.dialog
      .open<ConfirmationDialog, ConfirmationDialogData>(ConfirmationDialog, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().xs,
        data: {
          title: 'dm.deleteDocuments.title',
          description: 'dm.deleteDocuments.description',
        },
      })
      .afterClosed()
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() => this.dmService.deleteDocuments(rows as EntityTableRow[])),
      );
  }
}
